import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import '../styles/global.css';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('email'); // email | sent | reset | done
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (token && step === 'email') setStep('reset');

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setStep('sent');
    } catch { setError('No account found with this email address.'); }
    finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setStep('done');
    } catch { setError('This link has expired. Please request a new one.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ background: 'var(--teal)', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ marginBottom: '28px', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '24px', color: 'white' }}>
        BUMP <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
      </div>

      <div className="card" style={{ maxWidth: '420px', width: '100%' }}>
        {step === 'email' && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '6px' }}>Reset your password</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginBottom: '28px', lineHeight: '1.5' }}>Enter your email and we'll send you a reset link.</p>
            {error && <p style={{ color: 'var(--coral-dark)', fontSize: '13px', marginBottom: '16px', background: 'var(--coral-light)', padding: '10px 14px', borderRadius: '8px' }}>{error}</p>}
            <form onSubmit={handleRequest}>
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <button className="btn-primary" type="submit" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
            </form>
            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px' }}>
              <span style={{ color: 'var(--teal)', cursor: 'pointer', fontWeight: 700 }} onClick={() => navigate('/login')}>← Back to login</span>
            </p>
          </>
        )}

        {step === 'sent' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ width: '72px', height: '72px', background: 'var(--teal-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '3px solid var(--teal)' }}>
                <span style={{ fontSize: '28px' }}>✉</span>
              </div>
              <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '8px' }}>Check your email</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-soft)', lineHeight: '1.6' }}>We've sent a reset link to <strong style={{ color: 'var(--teal-dark)' }}>{email}</strong>.</p>
            </div>
            <div style={{ background: 'var(--teal-light)', borderRadius: '12px', padding: '14px 18px', fontSize: '13px', color: 'var(--teal-dark)' }}>
              The link expires in 1 hour. If you don't see it, check your spam folder.
            </div>
            <button className="btn-secondary" style={{ marginTop: '20px' }} onClick={() => setStep('email')}>Try a different email</button>
          </>
        )}

        {step === 'reset' && (
          <>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '6px' }}>Choose a new password</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginBottom: '28px', lineHeight: '1.5' }}>Enter your new password below.</p>
            {error && <p style={{ color: 'var(--coral-dark)', fontSize: '13px', marginBottom: '16px', background: 'var(--coral-light)', padding: '10px 14px', borderRadius: '8px' }}>{error}</p>}
            <form onSubmit={handleReset}>
              <div className="form-group">
                <label>New Password</label>
                <input type="password" placeholder="At least 8 characters" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Confirm New Password</label>
                <input type="password" placeholder="Repeat your password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
              </div>
              <button className="btn-primary" type="submit" disabled={loading}>{loading ? 'Saving...' : 'Set New Password'}</button>
            </form>
          </>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '72px', height: '72px', background: 'var(--teal-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '3px solid var(--teal)' }}>
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M5 15L11 21L25 7" stroke="#539390" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '22px', color: 'var(--teal-dark)', marginBottom: '8px' }}>Password updated!</h2>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginBottom: '28px' }}>Your password has been changed. You can now log in.</p>
            <button className="btn-primary" onClick={() => navigate('/login')}>Log In</button>
          </div>
        )}
      </div>
    </div>
  );
}
